import { useHandler } from "@chatbotgang/etude/react/useHandler";
import { css } from "@emotion/react";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid2";
import Stack from "@mui/material/Stack";
import Tab from "@mui/material/Tab";
import type { TabsProps } from "@mui/material/Tabs";
import Tabs from "@mui/material/Tabs";
import TextField from "@mui/material/TextField";
import { theme } from "@polifonia/theme";
import { Link, useNavigate } from "@tanstack/react-router";
import { useSnackbar } from "notistack";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { Button } from "@/components/Button";
import { Typography } from "@/components/Typography";
import type { InterludeTypes } from "@/interlude";
import { interlude } from "@/interlude";
import { DownloadTemplates } from "@/pages/applications/pages/productFeeds/components/DownloadTemplates";
import { FeedNameField } from "@/pages/applications/pages/productFeeds/components/FeedNameField";
import { FileDropZone } from "@/pages/applications/pages/productFeeds/components/FileDropZone";
import { SyncFrequencyField } from "@/pages/applications/pages/productFeeds/components/SyncFrequencyField";
import { useFileUpload } from "@/pages/applications/pages/productFeeds/hooks/useFileUpload";
import { useUrlFeedForm } from "@/pages/applications/pages/productFeeds/pages/add/useUrlFeedForm";
import { PageLayout } from "@/pages/components/PageLayout";
import { PageTitle } from "@/pages/components/PageTitle";

type ProductFeedSourceType = InterludeTypes["ProductFeed"]["sourceType"];

const cssDescription = css`
  color: ${theme.colors.staticFgNote};
`;

const cssFieldLabel = css`
  color: ${theme.colors.staticFgPrimary};
`;

const cssHeaderContainer = css`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  margin-bottom: 24px;
`;

const cssBackButton = css`
  text-decoration: none;
`;

const cssFormContainer = css`
  display: flex;
  flex-direction: column;
  gap: 24px;
  max-width: 600px;
  width: 100%;
`;

const cssButtonContainer = css({
  display: "flex",
  gap: "12px",
  marginTop: "8px",
});

interface TabPanelProps {
  children?: React.ReactNode;
  index: ProductFeedSourceType;
  value: ProductFeedSourceType;
}

const TabPanel: React.FC<TabPanelProps> = ({
  children,
  value,
  index,
  ...other
}) => {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`add-feed-tabpanel-${index}`}
      aria-labelledby={`add-feed-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 1 }}>{children}</Box>}
    </div>
  );
};

const tabValueMap: Record<ProductFeedSourceType, number> = {
  file_upload: 0,
  feed_url: 1,
} as const;

const tabIndexMap: Record<number, ProductFeedSourceType> = {
  0: "file_upload",
  1: "feed_url",
} as const;

const FileUploadForm: React.FC = function FileUploadForm() {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const [feedName, setFeedName] = useState("");

  const fileUpload = useFileUpload();

  const createFeedMutation = interlude.productFeed.useCreateFeed();

  const isSubmitting = fileUpload.isUploading || createFeedMutation.isPending;

  const handleSubmit = useHandler(async () => {
    if (!feedName.trim()) {
      enqueueSnackbar(t("productFeed.fileUpload.error.nameRequired"), {
        variant: "error",
      });
      return;
    }

    if (!fileUpload.selectedFile) {
      enqueueSnackbar(t("productFeed.fileUpload.error.fileRequired"), {
        variant: "error",
      });
      return;
    }

    try {
      const gcsUri = await fileUpload.uploadFile(fileUpload.selectedFile);

      await createFeedMutation.mutateAsync({
        name: feedName.trim(),
        feedUrl: gcsUri,
        sourceType: "file_upload",
        filename: fileUpload.selectedFile.name,
      });

      // Success: Navigate to list page
      navigate({ to: "/applications/product-feeds" });
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : String(error);
      enqueueSnackbar(
        `${t("productFeed.fileUpload.error.uploadFailed")}: ${errorMessage}`,
        {
          variant: "error",
        },
      );
    }
  });

  return (
    <Stack css={cssFormContainer}>
      <Typography variant="body" css={cssDescription}>
        {t("productFeed.fileUpload.description")}
      </Typography>

      <FeedNameField
        value={feedName}
        onChange={setFeedName}
        disabled={isSubmitting}
        translationPrefix="fileUpload"
      />

      <DownloadTemplates />

      <FileDropZone
        selectedFile={fileUpload.selectedFile}
        disabled={isSubmitting}
        onFileSelect={fileUpload.handleFileSelect}
        onRemoveFile={fileUpload.handleRemoveFile}
      />

      <div css={cssButtonContainer}>
        <Button
          variant="primary"
          onClick={handleSubmit}
          loading={isSubmitting}
          disabled={
            !feedName.trim() || !fileUpload.selectedFile || isSubmitting
          }
        >
          {t("productFeed.fileUpload.uploadButton")}
        </Button>
      </div>
    </Stack>
  );
};

const UrlFeedForm: React.FC = function UrlFeedForm() {
  const { t } = useTranslation();
  const {
    feedName,
    setFeedName,
    feedUrl,
    setFeedUrl,
    syncFrequency,
    setSyncFrequency,
    isSubmitting,
    handleSubmit,
  } = useUrlFeedForm();

  return (
    <Stack css={cssFormContainer}>
      <Typography variant="body" css={cssDescription}>
        {t("productFeed.urlFeed.description")}
      </Typography>

      <FeedNameField
        value={feedName}
        onChange={setFeedName}
        disabled={isSubmitting}
        translationPrefix="urlFeed"
      />

      <DownloadTemplates />

      <Stack direction="column" gap={1}>
        <Typography variant="body" css={cssFieldLabel}>
          {t("productFeed.urlFeed.feedUrl")}
        </Typography>
        <TextField
          placeholder={t("productFeed.urlFeed.feedUrlPlaceholder")}
          value={feedUrl}
          onChange={(e) => setFeedUrl(e.target.value)}
          fullWidth
          required
          disabled={isSubmitting}
          type="url"
        />
      </Stack>

      <SyncFrequencyField value={syncFrequency} onChange={setSyncFrequency} />

      <div css={cssButtonContainer}>
        <Button
          variant="primary"
          onClick={handleSubmit}
          loading={isSubmitting}
          disabled={!feedName.trim() || !feedUrl.trim() || isSubmitting}
        >
          {t("productFeed.urlFeed.createButton")}
        </Button>
      </div>
    </Stack>
  );
};

export const AddFeedPage: React.FC = function AddFeedPage() {
  const { t } = useTranslation();
  const [selectedType, setSelectedType] =
    useState<ProductFeedSourceType>("file_upload");

  const tabValue = tabValueMap[selectedType];

  const handleChange = useHandler<TabsProps["onChange"]>(
    (_, newValue: number) => {
      const newType = tabIndexMap[newValue as keyof typeof tabIndexMap];
      if (newType) {
        setSelectedType(newType);
      }
    },
  );

  return (
    <PageLayout>
      <Grid container css={cssHeaderContainer}>
        <Grid>
          <PageTitle>{t("productFeed.page.title")}</PageTitle>
        </Grid>
        <Grid>
          <Link to="/applications/product-feeds" css={cssBackButton}>
            <Button variant="plain">{t("productFeed.backToOverview")}</Button>
          </Link>
        </Grid>
      </Grid>
      <Tabs value={tabValue} onChange={handleChange} aria-label="add feed tabs">
        <Tab
          label={t("productFeed.page.fileUpload.tabTitle")}
          id={`add-feed-tab-file_upload`}
          aria-controls={`add-feed-tabpanel-file_upload`}
        />
        <Tab
          label={t("productFeed.page.urlFeed.tabTitle")}
          id={`add-feed-tab-feed_url`}
          aria-controls={`add-feed-tabpanel-feed_url`}
        />
      </Tabs>
      <TabPanel value={selectedType} index="file_upload">
        <FileUploadForm />
      </TabPanel>
      <TabPanel value={selectedType} index="feed_url">
        <UrlFeedForm />
      </TabPanel>
    </PageLayout>
  );
};
